/**
 * Globale Suche (Strg K, Story 18.13, EXPERIENCE §4.1): Vertrag einer Such-Quelle. Jeder Besitzer (orders, documents,
 * customers, catalog) veröffentlicht eine View `report.<view>` mit den Spalten `id, title, subtitle, on_date, sort_at,
 * run_mode` und seinen Suchfeldern (Trigramm-Index auf der Such-Faltung, `foldSearchSql`) und meldet sie mit diesem
 * Vertrag an; `reporting` sucht nur über diese Views (AD-1). Rein, ohne Datenbank: auch die Web-App nutzt die Typen.
 */

/** Gruppen der Suche in Anzeige-Reihenfolge (EXPERIENCE §4.1): Kunden, Aufträge, Belege, Artikel. */
export const searchGroups = ['customers', 'orders', 'documents', 'articles'] as const;
export type SearchGroup = (typeof searchGroups)[number];

/** Eine Such-Quelle (Beitrag des Besitzers). */
export interface SearchSource {
  /** Gruppe in der Trefferliste. */
  group: SearchGroup;
  /** Deutsche Überschrift der Gruppe, z. B. „Kunden“. */
  label: string;
  /** Besitzer-Modul der View (AD-2). */
  module: string;
  /** View im Schema `report`, Name `search_<gruppe>__<modul>`. Bezeichner nur aus dem Code. */
  view: string;
  /** Suchfelder der View (Text); jeder Begriff muss in mindestens einem davon stehen. */
  fields: readonly string[];
  /** Feld mit der Nummer: Ein Treffer, dessen Nummer genau einem Begriff entspricht, steht vorn. */
  numberField: string;
}

/**
 * Hinweis eines anderen Besitzers an Treffern einer Gruppe (Story 12.10: billing hängt „ersetzt durch <Neu-Nr>“ an die
 * ersetzte Rechnung in der Gruppe Belege). View `report.search_hint_<gruppe>__<modul>` mit den Spalten `id` (Schlüssel
 * des Treffers der Gruppe) und `value`; `reporting` hängt `hint(value)` an den Untertitel des Treffers. So liest der
 * Besitzer der Treffer (z. B. documents) nie das Schema des Hinweis-Besitzers (AD-1/AD-2), und der Treffer bleibt einer.
 */
export interface SearchHintSource {
  /** Gruppe, an deren Treffer der Hinweis kommt. */
  group: SearchGroup;
  /** Besitzer-Modul der View (AD-2). */
  module: string;
  /** View im Schema `report`, Name `search_hint_<gruppe>__<modul>`. Bezeichner nur aus dem Code. */
  view: string;
  /** Deutscher Hinweis aus dem Wert der View, z. B. „ersetzt durch 20278117“. */
  hint: (value: string) => string;
}

/** Ein Treffer der globalen Suche. */
export interface SearchHit {
  id: string;
  title: string;
  subtitle: string | null;
  /** Fachliches Datum (ISO `YYYY-MM-DD`, Europe/Berlin), z. B. Bestell- oder Belegdatum. */
  onDate: string | null;
  /** Probephase-Datensatz (Laufmodus `probe`): trägt „Probe“. */
  probe: boolean;
  /** Nummer entspricht genau einem Suchbegriff. */
  exact: boolean;
}

/** Treffer einer Gruppe (höchstens so viele wie die Einstellung „Treffer je Gruppe der globalen Suche“; `more` = es gibt weitere). */
export interface SearchGroupResult {
  group: SearchGroup;
  label: string;
  hits: SearchHit[];
  more: boolean;
}

/** Feste Texte der Suche (Oberfläche und Fehlermeldungen Deutsch, EXPERIENCE §5.3). */
export const SEARCH_TEXTS = {
  placeholder: 'Suchen – Nummer, Kunde oder Artikel …',
  tooShort: (min: number) => `Mindestens ${min} Zeichen eingeben.`,
  tooLong: (max: number) => `Der Suchbegriff ist zu lang (höchstens ${max} Zeichen).`,
  timeout: 'Die Suche dauert zu lange – bitte den Begriff genauer eingeben.',
  tooManyTerms: (max: number) => `Bitte höchstens ${max} Begriffe eingeben.`,
  nothingFound: (q: string) => `Nichts gefunden für ‚${q}‘.`,
  more: 'Weitere Treffer – Suchbegriff genauer eingeben.',
  probe: 'Probe',
  commands: 'Befehle',
} as const;

/** Begriffe eines Suchtexts: an Leerzeichen getrennt, ohne leere. */
export function searchTerms(q: string): string[] {
  return q.trim().split(/\s+/).filter((t) => t.length > 0);
}
